import useQueryParams from 'utils/hooks/useQueryParams'
import { useCallback, useEffect, useMemo, useState } from 'react'
import prepareFilterParams from 'utils/prepareFilterParams'
import { useDispatch } from 'react-redux'
import pick from 'lodash/pick'
import pickBy from 'lodash/pickBy'
import { parse } from 'date-fns'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import format from 'utils/dateFns/format'
import { defaultStaticRanges } from 'components/DatePicker/defineds'

const MIN_DATE = '2018-01-01'
const REGEXP_DATE = new RegExp(/^\d{4}-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/)

const DEFAULT_PRESET_NAME = 'last_thirty_days'

const useFiltersManager = ({
  filters,
  config: initialConfig = { initial: {} },
  setAction = '',
  additionalFilterState = {},
  nonClearableFilters = [],
  localStorageKey = null,
  ignoreQuery = false,
}) => {
  const [initialized, setInitialized] = useState(false)
  const [lsData, setLsData] = useLocalStorage('filterPanelConfigs', {})
  const { query, setQuery } = useQueryParams()
  const dispatch = useDispatch()
  const [params, setParams] = useState()
  const [config, setConfig] = useState(initialConfig)
  const staticRanges = useMemo(() => defaultStaticRanges(() => {}, 'ru'), [])

  const parseTime = useCallback((date) => parse(date, 'yyyy-MM-dd', new Date()).getTime(), [])

  const [storedPresetsNames] = useLocalStorage('filterDatepickerPresets', {})

  //Инициализируемся из query первостепенно, если не обнаружили - берем из первичного конфига,
  // а если есть filterDatepickerPresets в LocalStorage по ключу этой панели/раздела и нет в URL - из пресета
  const getDates = useCallback(
    ({ date_from, date_to }) => {
      const hasQueryDates = !!(date_to && date_from)

      if (hasQueryDates) {
        return { date_from, date_to }
      }

      //Если в config.initial строго стоят null - значит инициализируем датапикер пустым
      if (config.initial.date_to === null && config.initial.date_from === null) {
        return { date_to: undefined, date_from: undefined }
      }

      const dates =
        localStorageKey && lsData?.[localStorageKey]
          ? ({ date_to, date_from } = lsData[localStorageKey])
          : {}

      const { date_to: config_date_to = null } = config.initial

      // if (!config_date_to) {
      //   return dates
      // }

      if (
        date_from &&
        (Date.parse(date_from) < 0 ||
          parseTime(date_from) < parseTime(MIN_DATE) ||
          !date_from.match(REGEXP_DATE))
      ) {
        date_from = config_date_to
      }

      if (
        date_to &&
        (Date.parse(date_to) < 0 ||
          parseTime(date_to) < parseTime(MIN_DATE) ||
          !date_to.match(REGEXP_DATE))
      ) {
        date_to = config_date_to
      }

      if (date_from && date_to && parseTime(date_from) > parseTime(date_to)) {
        date_from = date_to
      }

      if (date_from) {
        dates.date_from =
          parseTime(date_from) > parseTime(config_date_to) ? config_date_to : date_from
      }

      if (date_to) {
        dates.date_to = parseTime(date_to) > parseTime(config_date_to) ? config_date_to : date_to
      }

      if (!hasQueryDates) {
        //restore dates from LS if it exists in 'filterDatepickerPresets' for current pathname
        const presetName = storedPresetsNames[global.location.pathname] || DEFAULT_PRESET_NAME

        const range = staticRanges.map((sr) => sr.range()).find((r) => r.preset_name === presetName)

        if (range) {
          dates.date_from = format(range.startDate, 'yyyy-MM-dd')
          dates.date_to = format(range.endDate, 'yyyy-MM-dd')
        }
      }

      return dates
    },
    [config.initial],
  )

  const getMergedConfig = (config = { initial: {} }) => {
    const { date_from = null, date_to = null, ...rest } = ignoreQuery ? {} : query

    let merged = {
      ...config.initial,
      ...pick(
        {
          ...additionalFilterState,
          ...rest,
          ...getDates({ date_from, date_to }),
        },
        Object.keys(config.initial),
      ),
    }

    merged = Object.entries(merged).reduce((acc, [key, value]) => {
      let setValue = value

      const availableOptions = config.configuration?.[key]?.options

      if (availableOptions) {
        if (Array.isArray(value)) {
          setValue = value.reduce((acc, val) => {
            if (availableOptions.includes(val)) {
              acc = acc.concat([val])
            }
            return acc
          }, [])
        } else {
          setValue = availableOptions.includes(value) ? value : config.initial?.[key]
        }
      }

      acc[key] = setValue

      return acc
    }, {})

    return merged
  }

  const setupFilters = useCallback((config) => {
    // склеиваем конфиг инициализации и поля из параметров URL, которые нашлись в этом конфиге, иные отбрасываем
    const merged = getMergedConfig(config)

    //устанавливаем значения в redux (формат payload {fieldName, value} или массив аналогичных объектов)
    dispatch({
      type: setAction,
      payload: Object.entries(merged).map(([key, value]) => ({
        fieldName: key,
        value,
      })),
    })
    setInitialized(true)
  }, [])

  useEffect(() => {
    if (!filters) {
      setupFilters(config)
    } else {
      setInitialized(true)
    }
  }, [])
  const updateFilter = (payload) => {
    dispatch({
      type: setAction,
      payload: Array.isArray(payload) ? payload : [payload],
    })
  }

  // TODO
  // Специально отдельный апдейт конфигурации чтобы можно было в динамике подкидывать опции
  // возможно надо еще добавить чистку значений фильтров если выбранное значение не соответствует ни одной новой опции

  const updateFilterConfigurationByKey = useCallback(
    ({ key, val }) => {
      config.configuration &&
        setConfig((config) => ({
          ...config,
          configuration: {
            ...config.configuration,
            [key]: { ...config.configuration[key], ...val },
          },
        }))
    },
    [config.configuration],
  )

  const reInit = useCallback(
    ({ config }) => {
      setConfig(() => config)
    },
    [setConfig],
  )

  const clearFilter = (key) => {
    dispatch({
      type: setAction,
      payload: [
        {
          fieldName: key,
          value: config.initial[key],
        },
      ],
    })
  }

  const clearFilters = () => {
    dispatch({
      type: setAction,
      payload: Object.entries(
        pickBy(config.initial, (value, key) => !nonClearableFilters.includes(key)),
      ).map(([key, value]) => ({
        fieldName: key,
        value,
      })),
    })
  }

  useEffect(() => {
    if (!filters) return

    const params = prepareFilterParams(filters)
    !ignoreQuery && setQuery(params)
    setParams(params)
    localStorageKey && setLsData({ ...lsData, [localStorageKey]: { ...filters } })
  }, [filters])

  return {
    filters,
    params,
    updateFilter,
    clearFilter,
    clearFilters,
    updateFilterConfigurationByKey,
    reInit,
    configuration: config.configuration,
    initial: config.initial,
    getMergedConfig,
    initialized,
  }
}

export default useFiltersManager
